import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { apiFetch, ApiError } from '../../lib/api';
import { CommandStack, type Command } from './commands';
import type { SceneRef } from './scene-key';
import {
  SceneSaveQueue,
  type GeometryResult,
  type PendingGeometryOp,
  type SaveOutcome,
} from './save-queue';
import type { LayoutLeaseInfo } from './types';

export type { GeometryOp, GeometryResult, PendingGeometryOp, SaveOutcome } from './save-queue';
export type { SceneRef } from './scene-key';

/** 架构书 §10.3：几何变更 debounce 400ms 后批量提交 */
export const SAVE_DEBOUNCE_MS = 400;
/** 租约 TTL 是 60s（后端 LEASE_TTL_SECONDS），心跳取其一半留出重试余量 */
const LEASE_HEARTBEAT_MS = 30_000;

export type LeaseState = 'idle' | 'mine' | 'held' | 'denied';

/**
 * 场景编辑租约（D-07）。
 *
 * 租约只是**协作提示**，不是安全边界：后端在每次几何写入时独立判定归属，
 * 前端拿不到租约时只是切成只读呈现，绝不是"前端说了算"。
 *
 * M3B 独立复审第二轮 NEW-2 现场复测时发现的同族问题（不在原始 NEW-2 清单里，
 * 但根因完全一样）：这里原本只依赖 `[parentId, enabled, tick]`——`X-Warehouse-
 * Id` 请求头由 `apiFetch` 在**真正发请求那一刻**同步读取当前仓库，不是这个
 * effect 自己的状态，所以切换仓库但 `parentId` 不变（两个仓库的根场景都是
 * `parentId=null`，M3B 里最常见的情况）时，这个 effect 根本不会重新跑：不会
 * 释放旧仓库的租约，也不会为新仓库重新 acquire，`state`/`holder` 会一直显示
 * 切仓库之前那个仓库的租约状态——真实复现过一次："仓库 A 的租约被对手抢走"
 * 之后切到完全无关的仓库 B，UI 却还在说"Rival 正在编辑此布局"，而仓库 B
 * 里根本不存在这场租约竞争。依赖数组补上 `warehouseId` 后，切仓库会触发
 * cleanup（释放旧仓库的租约）+ 重新 acquire（新仓库），行为跟切场景
 * （`parentId` 变化）完全对称。
 */
export function useSceneLease(params: {
  readonly warehouseId: string | undefined;
  readonly parentId: string | null;
  readonly enabled: boolean;
}): {
  readonly state: LeaseState;
  readonly holder: LayoutLeaseInfo | null;
  readonly takeover: () => Promise<void>;
  readonly refresh: () => void;
} {
  const { warehouseId, parentId, enabled } = params;
  const [state, setState] = useState<LeaseState>('idle');
  const [holder, setHolder] = useState<LayoutLeaseInfo | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setState('idle');
      return;
    }
    let cancelled = false;

    const acquire = async (): Promise<void> => {
      try {
        const lease = await apiFetch<LayoutLeaseInfo>('/layouts/lease', {
          method: 'POST',
          body: { parentId },
        });
        if (cancelled) return;
        setHolder(lease);
        setState('mine');
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === 'LAYOUT.LEASE_HELD_BY_ANOTHER_USER') {
          setHolder({
            userId: '',
            userName: String(err.params.name ?? ''),
            expiresAt: String(err.params.expiresAt ?? ''),
            isMine: false,
          });
          setState('held');
        } else {
          // 没有 layout.edit 的用户就是只读模式，不是错误状态（UX 设计书 S4）
          setState('denied');
        }
      }
    };

    void acquire();
    const timer = window.setInterval(() => void acquire(), LEASE_HEARTBEAT_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      // 主动释放：用户离开场景（或切仓库）后别人不用等 60s 过期
      void apiFetch('/layouts/lease/release', { method: 'POST', body: { parentId } }).catch(
        () => undefined,
      );
    };
  }, [warehouseId, parentId, enabled, tick]);

  const takeover = useCallback(async () => {
    const lease = await apiFetch<LayoutLeaseInfo>('/layouts/lease/takeover', {
      method: 'POST',
      body: { parentId },
    });
    setHolder(lease);
    setState('mine');
  }, [parentId]);

  return { state, holder, takeover, refresh: () => setTick((n) => n + 1) };
}

/**
 * 几何批量保存队列（架构书 §10.3 + M3a 独立复审 MEDIUM-1 修复）。
 *
 * 场景隔离的纯逻辑在 `save-queue.ts` 的 `SceneSaveQueue`（可独立单测，见
 * `save-queue.test.ts`）；**这个 hook 的职责是把"离开场景就得冲掉那个场景
 * 自己的待保存操作"这条规则钉在 React 生命周期上，而不是指望调用方（页面
 * 组件）自己记得写正确的 effect**——MEDIUM-1 的根因正是旧实现把这份责任
 * 交给了页面，页面的 effect 没覆盖到"卸载"和"场景切换"两个时机。
 *
 * 因此这里直接接收当前 `warehouseId`/`parentId` 作为参数：
 *   - 两者之一变化（场景切换或**仓库切换**——M3B 独立复审第二轮 NEW-2：
 *     仓库切换时 `parentId` 经常不变，两个仓库的根场景都是 `parentId=null`，
 *     不能只看 `parentId`）时，effect 的 cleanup 用的是变化前那次渲染捕获的
 *     旧值——天然就是"正在离开的场景"，立即 flush 它。
 *   - 组件整体卸载时，两个 cleanup 都会跑（先 flush 当前场景，再
 *     `flushAll()` 兜底扫一遍其他残留场景），不存在"卸载时什么都没发生"。
 *   - 真正的浏览器级卸载（刷新/关标签/外链跳转）不会触发上面两个 effect 的
 *     cleanup——那两个只覆盖 SPA 内部的场景切换/路由卸载。`beforeunload` /
 *     `pagehide` 因此单独处理：既弹确认框留人，也主动 `flushAll()` 补救
 *     （M3B 独立复审 H-1 修复，详见下面那个 effect 自己的注释）。
 */
export function useGeometrySaver(
  warehouseId: string | undefined,
  parentId: string | null,
  params: {
    readonly onSettled: (scene: SceneRef, outcome: SaveOutcome) => void;
    /**
     * 发送时刻的版本权威（M3B 独立复审第三轮 BLOCKER 2，见
     * `geometry-authority.ts`）：排队的操作不带 version，PATCH 发出前一刻
     * 才通过它解析最新已知版本。
     */
    readonly resolveVersion: (locationId: string) => number;
  },
): {
  readonly queue: (op: PendingGeometryOp) => void;
  readonly flushScene: (scene: SceneRef) => Promise<void>;
  readonly flushAll: () => Promise<void>;
  readonly saving: boolean;
  readonly hasAnyPending: boolean;
  readonly pendingCount: number;
} {
  const onSettledRef = useRef(params.onSettled);
  onSettledRef.current = params.onSettled;
  const resolveVersionRef = useRef(params.resolveVersion);
  resolveVersionRef.current = params.resolveVersion;

  const [, bump] = useReducer((n: number) => n + 1, 0);

  const queueRef = useRef<SceneSaveQueue | null>(null);
  if (!queueRef.current) {
    queueRef.current = new SceneSaveQueue({
      debounceMs: SAVE_DEBOUNCE_MS,
      resolveVersion: (locationId) => resolveVersionRef.current(locationId),
      send: (scene, ops) =>
        apiFetch<{ results: GeometryResult[] }>('/layouts/geometry', {
          method: 'PATCH',
          body: { parentId: scene.parentId, ops },
          // 场景切换/卸载触发的 flush 也要用这条路径送达（见 apiFetch 的 keepalive 说明）。
          keepalive: true,
        }),
      onSettled: (scene, outcome) => onSettledRef.current(scene, outcome),
      onPendingChange: () => bump(),
    });
  }
  const queue = queueRef.current;

  // 未选定仓库时（编辑器首次挂载、仓库选择还没落地）没有真正的场景可言——
  // 此时也不可能有可拖拽对象（`sceneQuery` 依赖 `!!warehouseId` 才会
  // enabled），这里只是给 SceneRef 一个合法的字符串占位，不代表真的会有
  // 操作在这个 key 下排队。
  const wh = warehouseId ?? '';

  // 场景/仓库切换：冲掉正在离开的那个场景（不是即将进入的新场景）。
  useEffect(() => {
    const leaving: SceneRef = { warehouseId: wh, parentId };
    return () => void queue.flushScene(leaving);
  }, [queue, wh, parentId]);

  // 卸载兜底：万一还有别的场景残留未保存操作，一次性全部冲掉。
  useEffect(() => {
    return () => {
      void queue.flushAll();
      queue.dispose();
    };
  }, [queue]);

  /**
   * 有未保存操作时真正离开浏览器（关标签/刷新/外链跳转）——SPA 内部路由跳不到
   * 这个事件，只拦得住真正的浏览器级卸载。
   *
   * M3B 独立复审 H-1：旧实现这里只 `preventDefault()` 弹一个"确定要离开吗"的
   * 确认框，从没有真的调用过 flush——`send()` 里声明的 `keepalive: true`
   * （见上面 queue 的初始化）因此从未在这条路径上被真正用到过：用户一旦点了
   * "仍然离开"，待保存的几何操作就随文档一起被丢弃，`flushAll()` 从未执行。
   *
   * 现在两件事都做：`preventDefault` 照常弹窗给用户一个"留下来"的机会；不管
   * 用户最终选哪个，都立即发起 `flushAll()`——`keepalive: true` 保证请求在
   * 文档卸载之后依然能送达。`pagehide` 在移动端 / 前进后退缓存路径下比
   * `beforeunload`更可靠，两边都挂不会重复发送：`SceneSaveQueue.flushScene`
   * 对已经清空的场景是安全空操作（见 `save-queue.test.ts`），谁先跑到，后来者
   * 都只是发现队列已经空了。
   */
  useEffect(() => {
    const flushOnLeave = (): void => {
      if (queue.hasAnyPending) void queue.flushAll();
    };
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      if (queue.hasAnyPending) {
        e.preventDefault();
        e.returnValue = '';
      }
      flushOnLeave();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', flushOnLeave);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', flushOnLeave);
    };
  }, [queue]);

  return {
    queue: useCallback(
      (op) => queue.queue({ warehouseId: wh, parentId }, op),
      [queue, wh, parentId],
    ),
    flushScene: useCallback((scene: SceneRef) => queue.flushScene(scene), [queue]),
    flushAll: useCallback(() => queue.flushAll(), [queue]),
    saving: queue.saving,
    hasAnyPending: queue.hasAnyPending,
    pendingCount: queue.pendingCount({ warehouseId: wh, parentId }),
  };
}

/** `CommandStack` 接到 React 生命周期上，暴露可直接绑定 UI 的只读状态。 */
export function useCommandStack(): {
  readonly execute: (cmd: Command) => Promise<void>;
  readonly undo: () => Promise<void>;
  readonly redo: () => Promise<void>;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly undoLabel: string | null;
  readonly redoLabel: string | null;
  readonly clear: () => void;
} {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const stackRef = useRef<CommandStack | null>(null);
  if (!stackRef.current) {
    stackRef.current = new CommandStack({ onChange: () => bump() });
  }
  const stack = stackRef.current;

  return {
    execute: useCallback((cmd: Command) => stack.execute(cmd), [stack]),
    undo: useCallback(() => stack.undo(), [stack]),
    redo: useCallback(() => stack.redo(), [stack]),
    canUndo: stack.canUndo,
    canRedo: stack.canRedo,
    undoLabel: stack.undoLabel,
    redoLabel: stack.redoLabel,
    clear: useCallback(() => stack.clear(), [stack]),
  };
}
